import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { SearchInput, useFetch } from "@4uhub/lib4uhub";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import {
  DragDropContext,
  Droppable,
  OnDragEndResponder,
} from "react-beautiful-dnd";

import { IListComponent } from "../../../../models/dashboard";
import DashBoardService from "../../../../services/dashboard.service";
import { useDashboard } from "../../../../store/contexts/DashBoardContext";
import { usePanel } from "../../../../store/contexts/DashBoardPannelContext";
import Component from "./Component";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";

const TRANSLATION_PATH = "components.panel.";

const service = new DashBoardService();

export interface IComponent extends IListComponent {
  order: number;
  dashBoardComponentId?: string;
}

const Components = ({ spaceBelow }: { spaceBelow: number | null }) => {
  const [components, setComponents] = useState<IListComponent[]>([]);

  const [search, setSearch] = useState("");

  const [checked, setChecked] = useState(false);

  const { languageId } = usePanel();

  const { t } = useTranslation();

  const confirm = useConfirm();

  // Ativar componente dispara o processamento do histórico dele em segundo plano — confirma antes.
  // Retorna false se o usuário cancelar.
  const confirmEnable = useCallback(
    async (all: boolean) => {
      try {
        await confirm({
          confirmationButtonProps: { variant: "contained" },
          title: t(
            TRANSLATION_PATH +
              (all ? "enable_component.title_all" : "enable_component.title"),
          ),
          description: t(
            TRANSLATION_PATH +
              (all
                ? "enable_component.description_all"
                : "enable_component.description"),
          ),
          cancellationText: t(TRANSLATION_PATH + "enable_component.cancel"),
          confirmationText: t(TRANSLATION_PATH + "enable_component.confirm"),
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    [confirm, t],
  );

  const {
    dashboard: {
      dashboardRootId,
      dashboardEstablishmentComponents,
      id: dashboardId,
    },
    updateComponentEnable,
    onDragComponents,
    addComponent,
  } = useDashboard();

  const { sendRequest: addComponentRequest } = useFetch(service.addComponent);

  const createComponent = useCallback(
    async (rootId: string) => {
      const { data, success } = await addComponentRequest({
        dashboardEstablishmentId: dashboardId,
        dashboardComponentId: rootId,
        languageId,
      });
      if (success && data) {
        addComponent(data);
      }
    },
    [addComponentRequest, addComponent, dashboardId, languageId],
  );

  const { sendRequest } = useFetch(service.listComponents);

  const { sendRequest: updateComponentEnableRequest } = useFetch(
    service.updateComponentEnable,
  );

  const { sendRequest: updateComponentsOrderRequest } = useFetch(
    service.updateComponentsOrder,
  );

  const fetchComponents = useCallback(async () => {
    const { data, success } = await sendRequest({
      DashboardId: dashboardRootId,
      languageId: languageId,
    });

    if (success && data) {
      setComponents(data);
    }
  }, [languageId, dashboardRootId, sendRequest]);

  const orderedComponents = useMemo(
    () =>
      components.map((d) => {
        const dashBoardComponent = dashboardEstablishmentComponents.find(
          (dc) => dc.componentRootId === d.id,
        );

        return {
          ...d,
          order: dashBoardComponent?.displayOrder || components.length + 1,
          dashBoardComponentId: dashBoardComponent?.id,
        };
      }),
    [dashboardEstablishmentComponents, components],
  );

  useEffect(() => {
    fetchComponents();
  }, [fetchComponents]);

  const onChangeHandler = useCallback(
    async (rootId: string, checked: boolean, componentId?: string) => {
      if (checked && !(await confirmEnable(false))) return;

      if (!componentId) {
        if (checked) await createComponent(rootId);
        return;
      }

      const { success } = await updateComponentEnableRequest({
        id: componentId,
        enable: checked,
      });
      if (success) {
        updateComponentEnable(componentId, checked);
      }
    },
    [
      updateComponentEnableRequest,
      updateComponentEnable,
      confirmEnable,
      createComponent,
    ],
  );

  const showMesage = useMemo(
    () => dashboardEstablishmentComponents.filter((d) => d.enable).length === 1,
    [dashboardEstablishmentComponents],
  );

  const onDragEnd: OnDragEndResponder = useCallback(
    async (e) => {
      const element = orderedComponents?.find((c) => e.draggableId === c.id);

      if (element) {
        if (e.destination) {
          orderedComponents.splice(e.source.index, 1);

          orderedComponents.splice(e.destination.index, 0, element);

          orderedComponents.forEach((t, i) => {
            t.order = i + 1;
          });

          setComponents(orderedComponents);

          onDragComponents(orderedComponents);

          updateComponentsOrderRequest({
            dashboardEstablishmentComponentIds: orderedComponents
              .filter((c) => c.dashBoardComponentId)
              .map((c) => c.dashBoardComponentId),
            dashboardEstablishmentId: dashboardId,
          });
        }
      }
    },
    [
      dashboardId,
      orderedComponents,
      onDragComponents,
      updateComponentsOrderRequest,
    ],
  );

  const checkAllHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
      if (value && !(await confirmEnable(true))) return;

      setChecked(value);

      orderedComponents.map(async (c) => {
        const componentId = dashboardEstablishmentComponents.find(
          (e) => e.componentRootId === c.id,
        )?.id;

        if (!componentId) {
          if (value) await createComponent(c.id);
          return c;
        }

        const { success } = await updateComponentEnableRequest({
          id: componentId,
          enable: value,
        });
        if (success) {
          updateComponentEnable(componentId, value);
        }

        return c;
      });
    },
    [
      orderedComponents,
      updateComponentEnableRequest,
      updateComponentEnable,
      confirmEnable,
      dashboardEstablishmentComponents,
      createComponent,
    ],
  );

  const filteredComponents = useMemo(
    () =>
      search.length > 0
        ? orderedComponents.filter((c) =>
            c.name.toLowerCase().includes(search.toLowerCase()),
          )
        : orderedComponents,
    [orderedComponents, search],
  );

  return (
    <Stack
      gap={2}
      padding={2}
      sx={{
        mb: spaceBelow ? 8 : undefined,
        maxHeight: spaceBelow ? spaceBelow : 400,
      }}
    >
      <FormControl>
        <FormLabel component="legend">
          {t(TRANSLATION_PATH + "select")}
        </FormLabel>

        <Stack sx={{ my: 2 }}>
          <SearchInput
            placeholder={t(TRANSLATION_PATH + "search")}
            onSearch={setSearch}
          />
        </Stack>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                onChange={checkAllHandler}
                checked={
                  dashboardEstablishmentComponents.every((e) => e.enable)
                    ? true
                    : checked
                }
              />
            }
            label={t(TRANSLATION_PATH + "select_all")}
            disabled={filteredComponents.length === 0}
            sx={(t) => ({
              width: "100%",
              mb: 2,
              pl: 1,
              pb: 1,
              borderBottom: `1px solid ${t.palette.divider}`,
            })}
          />

          {filteredComponents.length === 0 && (
            <Stack
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
              }}
            >
              <SearchOffRoundedIcon sx={{ fontSize: 40 }} />
              <Typography>Nenhum componente encontrado</Typography>
            </Stack>
          )}

          {filteredComponents.length > 0 && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="componentsList" direction="vertical">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {filteredComponents
                      .sort((a, b) => a.order - b.order)
                      .map((c, index) => (
                        <Component
                          key={c.id}
                          index={index}
                          data={c}
                          actualEstablishments={
                            dashboardEstablishmentComponents
                          }
                          onChange={onChangeHandler}
                        />
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </FormGroup>
        {showMesage && (
          <FormHelperText>{t("components.panel.helper_text")}</FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
};
export default memo(Components);
